import type { AllToolsResponse } from '@/features/traceability/api/TraceabilityResponseType';
import type { Tool } from '@/features/traceability/type/Tool';

type HealthStatus = 'Stable' | 'Surveillance' | 'Critique';

export type ToolPreview = {
  id: string;
  toolState: string; // 'new', 'worn', 'broken', etc.
  lastTraceDate: string | null; // ISO date string or null when no date is available
  traceCount: number;
};

export type ReferencePreview = {
  reference: string;
  family: string;
  health: HealthStatus;
  tools: ToolPreview[];
};

/**
 * Transform API response to component-compatible data structure
 *
 * Groups tools by reference and calculates health status based on tool states.
 *
 * @param apiResponse - AllToolsResponse from TraceabilityApiClient.toolReadAll()
 * @returns Array of ReferencePreview objects for display
 */
export function transformToolsData(apiResponse: AllToolsResponse): ReferencePreview[] {
  if (!apiResponse.tools || apiResponse.tools.length === 0) {
    return [];
  }

  // Group tools by reference
  const toolsByReference = new Map<string, Tool[]>();

  apiResponse.tools.forEach((tool) => {
    if (!tool) return;
    const refKey = tool.reference || 'Sans référence';

    if (!toolsByReference.has(refKey)) {
      toolsByReference.set(refKey, []);
    }

    toolsByReference.get(refKey)!.push(tool);
  });

  // Transform each reference group
  const referenceList: ReferencePreview[] = [];

  toolsByReference.forEach((tools, reference) => {
    const toolPreviews: ToolPreview[] = tools.map((tool) => ({
      id: tool.toolId,
      toolState: getToolState(tool),
      lastTraceDate: getLastTraceDate(tool),
      traceCount: tool.traces?.length ?? 0,
    }));

    referenceList.push({
      reference,
      family: deriveToolFamily(reference),
      health: calculateReferenceHealth(tools),
      tools: toolPreviews,
    });
  });

  // Sort by reference name
  referenceList.sort((a, b) => a.reference.localeCompare(b.reference));

  return referenceList;
}

/**
 * Extract tool state from latest trace
 * Returns the toolState value ('new', 'worn', 'broken', etc.)
 */
function getToolState(tool: Tool): string {
  if (!tool.traces || tool.traces.length === 0) {
    return 'unknown';
  }

  // Get the latest trace (assuming last in array)
  const latestTrace = tool.traces[tool.traces.length - 1];
  return latestTrace?.toolState?.toolState || 'unknown';
}

/**
 * Get the last trace date as ISO string.
 * Uses updatedAt from the most recent trace, falling back to createdAt.
 * Returns null when no traces exist or neither timestamp is set.
 */
function getLastTraceDate(tool: Tool): string | null {
  if (!tool.traces || tool.traces.length === 0) {
    return null;
  }

  const lastTrace = tool.traces[tool.traces.length - 1];
  return lastTrace.updatedAt ?? lastTrace.createdAt ?? null;
}

/**
 * Derive tool family from reference pattern
 *
 * Returns an i18n key (e.g. 'toolView.family.mill') instead of a raw French string.
 * Consumers must pass the key through t() for display.
 * Fallback to 'toolView.family.tool' if pattern not recognized.
 * This is a temporary solution until backend provides family field.
 */
function deriveToolFamily(reference: string): string {
  if (!reference || reference === 'Sans référence') {
    return 'toolView.family.tool';
  }

  // Simple pattern matching (can be enhanced based on actual reference patterns)
  const upper = reference.toUpperCase();

  if (upper.includes('FRAIS')) return 'toolView.family.mill';
  if (upper.includes('FORET')) return 'toolView.family.drill';
  if (upper.includes('TOUR')) return 'toolView.family.turning';
  if (upper.includes('ALÉS')) return 'toolView.family.reamer';
  if (upper.includes('TAR')) return 'toolView.family.tap';

  // Default fallback
  return 'toolView.family.tool';
}

/**
 * Calculate reference health status based on tool states
 *
 * Logic:
 * - 'Critique' if any tool is in Maintenance state
 * - 'Surveillance' if any tool is in Observation state (but none in Maintenance)
 * - 'Stable' if all tools are Active
 */
function calculateReferenceHealth(tools: Tool[]): HealthStatus {
  if (!tools || tools.length === 0) {
    return 'Stable';
  }

  let hasMaintenanceTools = false;
  let hasObservationTools = false;

  for (const tool of tools) {
    if (!tool.traces || tool.traces.length === 0) {
      hasObservationTools = true;
      continue;
    }

    const latestTrace = tool.traces[tool.traces.length - 1];
    const state = latestTrace?.toolState?.toolState;

    if (state === 'Maintenance') {
      hasMaintenanceTools = true;
      break; // No need to continue, already critical
    }

    if (state === 'Observation' || !state) {
      hasObservationTools = true;
    }
  }

  if (hasMaintenanceTools) return 'Critique';
  if (hasObservationTools) return 'Surveillance';
  return 'Stable';
}
